import { answerHeightKey, answerHeightKeyPrefix } from 'lib/answerRenderSurface'

export const MAX_REMEMBERED_ANSWER_HEIGHTS = 2000
export const ANSWER_HEIGHT_MEMORY_STORAGE_KEY = 'knov:answer-height-memory:v1'
// v2: AI-action posts render materially shorter (generation-card prompt clamp + input-media
// strip), so v1 measured heights would replay the old taller layout.
export const ANSWER_HEIGHT_MEMORY_STORAGE_VERSION = 2

const rememberedAnswerHeights = new Map()
let hasLoadedPersistedAnswerHeights = false
let pendingPersistHandle = null
let pendingPersistCancel = null

const toPositiveNumber = value => {
    const number = Math.ceil(Number(value || 0))
    return Number.isFinite(number) && number > 0 ? number : null
}

const getNow = () => {
    try {
        return Date.now()
    } catch {
        return 0
    }
}

const getStorage = () => {
    try {
        if (typeof window !== 'undefined' && window.localStorage) return window.localStorage
    } catch {}

    try {
        if (typeof globalThis !== 'undefined' && globalThis.localStorage)
            return globalThis.localStorage
    } catch {}

    return null
}

const cancelPendingPersist = () => {
    if (pendingPersistHandle == null || !pendingPersistCancel) return

    pendingPersistCancel(pendingPersistHandle)
    pendingPersistHandle = null
    pendingPersistCancel = null
}

const normalizeEntry = entry => {
    if (!entry || typeof entry.key !== 'string') return null

    const height = toPositiveNumber(entry.height)
    if (!height) return null

    const updatedAt = Number(entry.updatedAt || 0)
    return {
        key: entry.key,
        height,
        updatedAt: Number.isFinite(updatedAt) && updatedAt > 0 ? updatedAt : 0,
    }
}

const enforceRememberedAnswerHeightLimit = () => {
    while (rememberedAnswerHeights.size > MAX_REMEMBERED_ANSWER_HEIGHTS) {
        const oldestKey = rememberedAnswerHeights.keys().next().value
        if (!oldestKey) return
        rememberedAnswerHeights.delete(oldestKey)
    }
}

const setRememberedEntry = (key, height, updatedAt = getNow()) => {
    const rememberedHeight = toPositiveNumber(height)
    if (!key || !rememberedHeight) return false

    // Delete before set so Map iteration order remains least-recent to most-recent.
    rememberedAnswerHeights.delete(key)
    rememberedAnswerHeights.set(key, {
        height: rememberedHeight,
        updatedAt,
    })
    enforceRememberedAnswerHeightLimit()
    return true
}

const loadPersistedAnswerHeights = () => {
    if (hasLoadedPersistedAnswerHeights) return
    hasLoadedPersistedAnswerHeights = true

    const storage = getStorage()
    if (!storage) return

    try {
        const parsed = JSON.parse(storage.getItem(ANSWER_HEIGHT_MEMORY_STORAGE_KEY) || 'null')
        if (
            !parsed ||
            parsed.version !== ANSWER_HEIGHT_MEMORY_STORAGE_VERSION ||
            !Array.isArray(parsed.entries)
        ) {
            return
        }

        parsed.entries
            .map(normalizeEntry)
            .filter(Boolean)
            .forEach(entry => {
                setRememberedEntry(entry.key, entry.height, entry.updatedAt)
            })
    } catch {}
}

const persistRememberedAnswerHeights = () => {
    pendingPersistHandle = null
    pendingPersistCancel = null

    const storage = getStorage()
    if (!storage) return false

    let entries = Array.from(rememberedAnswerHeights, ([key, entry]) => ({
        key,
        height: entry.height,
        updatedAt: entry.updatedAt || 0,
    }))

    while (entries.length > 0) {
        try {
            storage.setItem(
                ANSWER_HEIGHT_MEMORY_STORAGE_KEY,
                JSON.stringify({
                    version: ANSWER_HEIGHT_MEMORY_STORAGE_VERSION,
                    entries,
                }),
            )
            return true
        } catch {
            entries = entries.slice(Math.ceil(entries.length / 2))
        }
    }

    try {
        storage.removeItem(ANSWER_HEIGHT_MEMORY_STORAGE_KEY)
    } catch {}
    return false
}

const schedulePersistRememberedAnswerHeights = () => {
    if (pendingPersistHandle != null) return
    if (!getStorage()) return

    const target =
        typeof window !== 'undefined'
            ? window
            : typeof globalThis !== 'undefined'
            ? globalThis
            : null
    if (!target) return

    if (typeof target.requestIdleCallback === 'function') {
        pendingPersistHandle = target.requestIdleCallback(persistRememberedAnswerHeights, {
            timeout: 1000,
        })
        pendingPersistCancel = handle => target.cancelIdleCallback?.(handle)
        return
    }

    pendingPersistHandle = target.setTimeout(persistRememberedAnswerHeights, 250)
    pendingPersistCancel = handle => target.clearTimeout(handle)
}

export const getRememberedAnswerHeight = (answerId, options = {}) => {
    if (!answerId) return null
    loadPersistedAnswerHeights()

    const width = toPositiveNumber(options?.width)
    if (width) {
        const exactEntry = rememberedAnswerHeights.get(answerHeightKey(answerId, options))
        if (exactEntry?.height) return exactEntry.height
        return null
    }

    const prefix = answerHeightKeyPrefix(answerId, options)
    let fallbackHeight = null
    for (const [key, entry] of rememberedAnswerHeights) {
        if (key.startsWith(prefix)) fallbackHeight = entry.height
    }

    return fallbackHeight
}

export const rememberAnswerHeight = (answerId, height, options = {}) => {
    const rememberedHeight = toPositiveNumber(height)
    const width = toPositiveNumber(options?.width)
    if (!answerId || !rememberedHeight || !width) return false
    loadPersistedAnswerHeights()

    const remembered = setRememberedEntry(
        answerHeightKey(answerId, {
            ...options,
            width,
        }),
        rememberedHeight,
    )
    if (!remembered) return false

    schedulePersistRememberedAnswerHeights()
    return true
}

export const flushRememberedAnswerHeightsForTest = () => {
    cancelPendingPersist()
    return persistRememberedAnswerHeights()
}

export const clearRememberedAnswerHeightsForTest = ({ clearStorage = true } = {}) => {
    cancelPendingPersist()
    rememberedAnswerHeights.clear()
    hasLoadedPersistedAnswerHeights = false

    if (!clearStorage) return

    try {
        getStorage()?.removeItem(ANSWER_HEIGHT_MEMORY_STORAGE_KEY)
    } catch {}
}
